import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Collapse,
  Chip,
  Stack,
  LinearProgress,
  Tooltip,
  Alert,
  Skeleton,
  Fade,
  Zoom,
  useTheme,
  alpha,
  Button,
  ButtonGroup,
  Divider,
  Paper,
  Tab,
  Tabs,
  Badge,
  Snackbar,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
  Refresh,
  ContentCopy,
  Info,
  Check,
  Edit,
  Visibility,
  Code,
  FormatSize,
  Save,
  Close,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

// Styled components for polished UI
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  overflow: 'visible',
  '&:hover': {
    boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.1)}`,
    borderColor: alpha(theme.palette.primary.main, 0.2),
  },
}));

const StyledTextArea = styled('textarea')(({ theme }) => ({
  width: '100%',
  minHeight: '300px',
  padding: theme.spacing(2),
  fontSize: '14px',
  fontFamily: 'Consolas, Monaco, "Andale Mono", "Ubuntu Mono", monospace',
  lineHeight: 1.5,
  border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
  borderRadius: theme.spacing(1),
  backgroundColor: theme.palette.mode === 'dark' 
    ? alpha(theme.palette.background.paper, 0.5)
    : alpha(theme.palette.background.paper, 0.9),
  color: theme.palette.text.primary,
  resize: 'vertical',
  transition: 'all 0.2s ease',
  '&:focus': {
    outline: 'none',
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.1)}`,
  },
  '&::placeholder': {
    color: alpha(theme.palette.text.secondary, 0.5),
  },
}));

const VariableChip = styled(Chip)(({ theme }) => ({
  background: `linear-gradient(45deg, ${theme.palette.primary.light} 30%, ${theme.palette.primary.main} 90%)`,
  color: theme.palette.primary.contrastText,
  fontWeight: 600,
  fontSize: '0.75rem',
  height: '24px',
  '& .MuiChip-label': {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
  color: theme.palette.primary.contrastText,
  fontWeight: 600,
  padding: theme.spacing(1, 3),
  transition: 'all 0.3s ease',
  '&:hover': {
    background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`,
    transform: 'translateY(-2px)',
    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
  },
}));

interface PromptEditorProps {
  module: 'module1' | 'module3';
  onPromptChange: (prompt: string | null) => void;
  maxTokens?: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`prompt-tabpanel-${index}`}
      aria-labelledby={`prompt-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Default prompts as fallback
const DEFAULT_PROMPTS = {
  module1: `You are an expert in keyword analysis and topic modeling. Your task is to analyze a list of keywords and identify:
1. Main topics that the keywords belong to
2. Subtopic categories within each main topic

For each topic and subtopic, provide:
- A clear, descriptive name
- A sampling of keywords that belong to that category

You MUST format your response as a valid JSON object with the following structure:
{
    "topics": [
        {
            "name": "Topic Name",
            "subtopics": [
                {
                    "name": "Subtopic Name",
                    "keywords": ["keyword1", "keyword2", "keyword3"]
                }
            ]
        }
    ]
}

IMPORTANT: Your response must be ONLY the JSON object, with no additional text, markdown formatting, or code blocks.
The JSON must be properly formatted with:
- All property names in double quotes
- All string values in double quotes
- No trailing commas
- No comments
- No line breaks within arrays or string values

Ensure that:
1. Topics and subtopics are based on subject matter themes
2. Each subtopic contains a representative sample of keywords (aim for 5-10 keywords per subtopic)
3. Include a "Brand" topic with appropriate subtopics based on the business context
4. Focus on creating clear, meaningful topic hierarchies that will help organize the keywords effectively`,
  module3: `You are an AI assistant specialized in semantic classification of keywords. Your task is to classify keywords into the provided topic framework categories based on their semantic meaning and relevance.

Given the topic framework and list of keywords, classify each keyword into the most appropriate category. Consider:
1. The semantic meaning of each keyword
2. The context provided by the topic framework
3. Industry-specific terminology and jargon

Format your response according to the specified structure.`
};

export default function PromptEditor({ module, onPromptChange, maxTokens = 4000 }: PromptEditorProps) {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [defaultPrompt, setDefaultPrompt] = useState<string>('');
  const [currentPrompt, setCurrentPrompt] = useState<string>('');
  const [isModified, setIsModified] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [fontSize, setFontSize] = useState<'small' | 'medium' | 'large'>('medium');
  const [tabValue, setTabValue] = useState(0);

  // Fetch default prompt
  useEffect(() => {
    const fetchDefaultPrompt = async () => {
      setLoading(true);
      setError(null);
      try {
        // Import the snowflakeApi from utils/api
        const { snowflakeApi } = await import('../utils/api');
        
        // Use the snowflakeApi to fetch the default prompt
        const response = await snowflakeApi.get(`/api/v1/prompts/defaults/${module}`);
        
        const data = response.data;
        setDefaultPrompt(data.prompt || '');
        setCurrentPrompt(data.prompt || '');
      } catch (err) {
        console.error('Error fetching default prompt:', err);
        
        // Use fallback prompt
        const fallbackPrompt = DEFAULT_PROMPTS[module];
        setDefaultPrompt(fallbackPrompt);
        setCurrentPrompt(fallbackPrompt);
        
        // Show a warning instead of error
        setError('Using fallback prompt. API endpoint not available.');
      } finally {
        setLoading(false);
      }
    };

    fetchDefaultPrompt();
  }, [module]);

  // Calculate estimated tokens (rough approximation)
  const estimatedTokens = useMemo(() => {
    // Rough estimate: 1 token ≈ 4 characters
    return Math.ceil(currentPrompt.length / 4);
  }, [currentPrompt]);

  const tokenPercentage = (estimatedTokens / maxTokens) * 100;
  const tokenColor = tokenPercentage > 90 ? 'error' : tokenPercentage > 70 ? 'warning' : 'success';

  // Extract template variables
  const templateVariables = useMemo(() => {
    const matches = currentPrompt.match(/\{\{([^}]+)\}\}/g);
    if (!matches) return [];
    return [...new Set(matches.map(match => match.replace(/[{}]/g, '')))];
  }, [currentPrompt]);

  const handlePromptChange = useCallback((newPrompt: string) => {
    setCurrentPrompt(newPrompt);
    const modified = newPrompt !== defaultPrompt;
    setIsModified(modified);
    onPromptChange(modified ? newPrompt : null);
  }, [defaultPrompt, onPromptChange]);

  const handleReset = useCallback(() => {
    setCurrentPrompt(defaultPrompt);
    setIsModified(false);
    onPromptChange(null);
  }, [defaultPrompt, onPromptChange]);

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(currentPrompt);
      setCopySuccess(true);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, [currentPrompt]);

  const handleSave = useCallback(() => {
    // In this implementation, we just show a success message
    // since we're not persisting to backend
    setSaveSuccess(true);
  }, []);

  const fontSizeMap = {
    small: '12px',
    medium: '14px',
    large: '16px',
  };

  const getPromptDiff = () => {
    if (!isModified) return null;
    
    // Simple diff visualization - in production, use a proper diff library
    const defaultLines = defaultPrompt.split('\n');
    const currentLines = currentPrompt.split('\n');
    const maxLines = Math.max(defaultLines.length, currentLines.length);
    
    return (
      <Box sx={{ fontFamily: 'monospace', fontSize: '12px' }}>
        {Array.from({ length: maxLines }).map((_, i) => {
          const defaultLine = defaultLines[i] || '';
          const currentLine = currentLines[i] || '';
          const isChanged = defaultLine !== currentLine;
          
          return (
            <Box key={i} sx={{ display: 'flex', minHeight: '20px' }}>
              <Box
                sx={{
                  flex: 1,
                  p: 0.5,
                  backgroundColor: isChanged && defaultLine ? alpha(theme.palette.error.main, 0.1) : 'transparent',
                  color: isChanged && defaultLine ? theme.palette.error.main : theme.palette.text.primary,
                  borderRight: 1,
                  borderColor: 'divider',
                }}
              >
                {defaultLine || ' '}
              </Box>
              <Box
                sx={{
                  flex: 1,
                  p: 0.5,
                  backgroundColor: isChanged && currentLine ? alpha(theme.palette.success.main, 0.1) : 'transparent',
                  color: isChanged && currentLine ? theme.palette.success.main : theme.palette.text.primary,
                }}
              >
                {currentLine || ' '}
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  if (loading) {
    return (
      <StyledCard>
        <CardContent>
          <Skeleton variant="text" width="60%" height={32} />
          <Skeleton variant="rectangular" height={200} sx={{ mt: 2 }} />
        </CardContent>
      </StyledCard>
    );
  }

  return (
    <>
      <StyledCard elevation={expanded ? 8 : 2}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={2} mb={2}>
            <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
              {module === 'module1' ? 'Module 1 Analysis' : 'Module 3 Semantic'} Prompt
            </Typography>
            
            {isModified && (
              <Zoom in={isModified}>
                <Badge
                  badgeContent="Modified"
                  color="warning"
                  sx={{
                    '& .MuiBadge-badge': {
                      fontSize: '0.7rem',
                      height: '20px',
                      minWidth: '60px',
                    },
                  }}
                />
              </Zoom>
            )}
            
            <IconButton
              onClick={() => setExpanded(!expanded)}
              size="small"
              sx={{
                transition: 'transform 0.3s ease',
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            >
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Stack>

          {error && (
            <Alert 
              severity={error.includes('fallback') ? 'warning' : 'error'} 
              sx={{ mb: 2 }}
            >
              {error}
            </Alert>
          )}

          <Collapse in={expanded} timeout="auto">
            <Paper variant="outlined" sx={{ mb: 2 }}>
              <Tabs
                value={tabValue}
                onChange={(_, newValue) => setTabValue(newValue)}
                variant="fullWidth"
                sx={{ borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab icon={<Edit />} label="Edit" />
                <Tab icon={<Visibility />} label="Preview" disabled={!isModified} />
                <Tab icon={<Code />} label="Variables" disabled={templateVariables.length === 0} />
              </Tabs>

              <TabPanel value={tabValue} index={0}>
                <Stack spacing={2}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <ButtonGroup size="small" variant="outlined">
                      <Button
                        onClick={() => setFontSize('small')}
                        variant={fontSize === 'small' ? 'contained' : 'outlined'}
                      >
                        <FormatSize fontSize="small" />
                      </Button>
                      <Button
                        onClick={() => setFontSize('medium')}
                        variant={fontSize === 'medium' ? 'contained' : 'outlined'}
                      >
                        <FormatSize />
                      </Button>
                      <Button
                        onClick={() => setFontSize('large')}
                        variant={fontSize === 'large' ? 'contained' : 'outlined'}
                      >
                        <FormatSize fontSize="large" />
                      </Button>
                    </ButtonGroup>

                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Copy to clipboard">
                        <IconButton size="small" onClick={handleCopy}>
                          <ContentCopy fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Reset to default">
                        <span>
                          <IconButton size="small" onClick={handleReset} disabled={!isModified}>
                            <Refresh fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Stack>

                  <StyledTextArea
                    value={currentPrompt}
                    onChange={(e) => handlePromptChange(e.target.value)}
                    placeholder="Enter your custom prompt here..."
                    style={{ fontSize: fontSizeMap[fontSize] }}
                  />

                  <Box>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="caption" color="text.secondary">
                        Character count: {currentPrompt.length}
                      </Typography>
                      <Typography
                        variant="caption"
                        color={tokenPercentage > 90 ? 'error' : tokenPercentage > 70 ? 'warning' : 'text.secondary'}
                      >
                        Estimated tokens: {estimatedTokens} / {maxTokens}
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(tokenPercentage, 100)}
                      color={tokenColor}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>

                  {isModified && (
                    <Fade in={isModified}>
                      <Stack direction="row" spacing={2} justifyContent="flex-end">
                        <Button
                          variant="outlined"
                          onClick={handleReset}
                          startIcon={<Close />}
                        >
                          Discard Changes
                        </Button>
                        <GradientButton
                          onClick={handleSave}
                          startIcon={<Save />}
                        >
                          Apply Changes
                        </GradientButton>
                      </Stack>
                    </Fade>
                  )}
                </Stack>
              </TabPanel>

              <TabPanel value={tabValue} index={1}>
                <Typography variant="subtitle2" gutterBottom>
                  Changes Comparison
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box
                  sx={{
                    maxHeight: '400px',
                    overflowY: 'auto',
                    backgroundColor: alpha(theme.palette.background.default, 0.5),
                    borderRadius: 1,
                    p: 1,
                  }}
                >
                  {getPromptDiff()}
                </Box>
              </TabPanel>

              <TabPanel value={tabValue} index={2}>
                <Typography variant="subtitle2" gutterBottom>
                  Template Variables Detected
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {templateVariables.map((variable) => (
                    <VariableChip
                      key={variable}
                      label={variable}
                      size="small"
                      icon={<Code />}
                    />
                  ))}
                </Stack>
                <Alert severity="info" sx={{ mt: 2 }}>
                  These variables will be replaced with actual values during analysis.
                </Alert>
              </TabPanel>
            </Paper>
          </Collapse>

          {!expanded && (
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                {isModified ? 'Custom prompt applied' : 'Using default prompt'}
              </Typography>
              <Tooltip title="Prompt customization allows you to modify the AI's analysis behavior">
                <Info fontSize="small" color="action" />
              </Tooltip>
            </Stack>
          )}
        </CardContent>
      </StyledCard>

      <Snackbar
        open={copySuccess}
        autoHideDuration={2000}
        onClose={() => setCopySuccess(false)}
        message="Prompt copied to clipboard!"
      />

      <Snackbar
        open={saveSuccess}
        autoHideDuration={2000}
        onClose={() => setSaveSuccess(false)}
        message="Prompt changes applied successfully!"
      />
    </>
  );
}